<template>
  <el-dialog
    class="dialog"
    :title="dialogTitle"
    :visible="visible"
    width="900px"
    :close-on-press-escape="true"
    :close-on-click-modal="false"
    :before-close="beforeClose"
  >
    <!-- 搜索表单 -->
    <el-form
      size="small"
      :inline="true"
      :model="searchForm"
      @submit.native.prevent
    >
      <el-form-item :label="searchLabel">
        <el-input
          v-model="searchForm[searchField]"
          :placeholder="searchPlaceholder"
          clearable
          @keyup.enter.native="search"
        />
      </el-form-item>
      <el-form-item>
        <el-button type="primary" @click="search">{{
          searchButtonText
        }}</el-button>
      </el-form-item>
    </el-form>

    <div v-if="tagProp" class="invite-tags">
      <el-tag
        v-for="(tag, index) in selectionRow"
        :key="tag[tableId]"
        size="medium"
        closable
        @close="closeTag(index)"
      >
        {{ tag[tagProp] }}
      </el-tag>
    </div>

    <!-- 多选表格 -->
    <jczh-table-content
      ref="table"
      :data="tableList"
      :option="tableOption"
      pagination
      :page.sync="listPage.page"
      :limit.sync="listPage.size"
      :total="listPage.total"
      selection
      :table-id="tableId"
      :selected="selectionRow"
      :selectable="selectable"
      @pagination="getList"
      @selection-change="changeSelection"
    >
      <template
        v-for="item in tableOption"
        :slot="item.slotName"
        slot-scope="{ row }"
      >
        <slot>
          <div v-if="item.slotName" :key="item.slotName">
            <slot :name="item.slotName" :row="row"></slot>
          </div>
        </slot>
      </template>
    </jczh-table-content>

    <span slot="footer" class="dialog-footer">
      <el-button size="small" type="primary" @click="confirm">确 定</el-button>
    </span>
  </el-dialog>
</template>

<script>
import { cloneDeep } from 'lodash'
import { isFunction } from 'element-ui/src/utils/types'

export default {
  name: 'DialogTable',

  model: {
    prop: 'visible',
    event: 'change'
  },

  props: {
    visible: {
      type: Boolean,
      default: false,
      required: true
    },

    // 弹窗标题
    dialogTitle: {
      type: String,
      default: '选择供应商'
    },

    // 搜索表单表头
    searchLabel: {
      type: String,
      default: '供应商'
    },

    // 搜索表单的属性值
    searchField: {
      type: String,
      default: 'supplier'
    },

    // 搜索输入框的提示文字
    searchPlaceholder: {
      type: String,
      default: '请输入供应商名称'
    },

    // 搜索按钮文字
    searchButtonText: {
      type: String,
      default: '搜索'
    },

    fetchParams: {
      type: Object,
      default: () => {}
    },

    // 表格数据请求接口
    tableFetch: {
      type: Function,
      default: () => {}
    },

    // 表格展示配置项
    tableOption: {
      type: Array,
      default: () => []
    },

    // 标签字段名称
    tagProp: {
      type: String,
      default: ''
    },

    // 主键
    tableId: {
      type: String,
      default: 'id'
    },

    // 禁止选择的数据项
    disabledArray: {
      type: Array,
      default: () => []
    },

    // 已选的数据项
    selectedArray: {
      type: Array,
      default: () => []
    },

    // 数据传入转换函数
    normalizeIn: {
      type: Function,
      default: list => {
        return list
      }
    },

    // 数据传出专函函数
    normalizeOut: {
      type: Function,
      default: list => {
        return list
      }
    }
  },

  data() {
    return {
      selectionRow: [],
      tableFetchParams: {},
      listPage: {
        total: 0,
        page: 1,
        size: 10
      },
      tableList: [],
      searchForm: {}
    }
  },

  computed: {
    selectable() {
      return row =>
        this.disabledArray.every(
          item => item[this.tableId] !== row[this.tableId]
        )
    }
  },

  watch: {
    fetchParams: {
      handler(val) {
        // 当传入的请求参数发生变化时，重新定义组件内的请求参数
        this.tableFetchParams = cloneDeep(val) || {}
        // this.getList()
      },
      immediate: true
    },

    visible: {
      handler(val) {
        if (val) {
          if (isFunction(this.normalizeIn)) {
            this.selectionRow = cloneDeep(this.normalizeIn(this.selectedArray))
          } else {
            this.selectionRow = cloneDeep(this.selectedArray)
          }

          this.getList()
        }
      }
    },

    selectedArray: {
      handler(val) {
        if (val.length === 0 && this.$refs.table) {
          this.$refs.table.clearSelection()
        }
        if (isFunction(this.normalizeIn)) {
          this.selectionRow = cloneDeep(this.normalizeIn(this.selectedArray))
        } else {
          this.selectionRow = cloneDeep(this.selectedArray)
        }
      },
      deep: true,
      immediate: true
    }
  },

  methods: {
    search() {
      // 接收搜索输入框的参数
      const keys = Object.keys(this.searchForm)
      keys.forEach(key => {
        this.tableFetchParams[key] = this.searchForm[key]
      })
      this.getList()
    },

    getList() {
      const tempParams = cloneDeep(this.tableFetchParams) || {}
      tempParams.page = this.listPage.page
      tempParams.size = this.listPage.size
      Promise.resolve(this.tableFetch(tempParams)).then(res => {
        if (res) {
          this.listPage.total = res.data?.total ?? 0
          this.tableList = res.data?.records || []
        }
      })
    },

    changeSelection(selection) {
      // 在父组件内保存可选表格传出的选中项数据
      this.selectionRow = selection
    },

    beforeClose() {
      this.closeDialog()
    },

    closeDialog() {
      this.$emit('change', false)
      // 重置已选项
      this.selectionRow = []
      // 重置列表的请求参数
      this.tableFetchParams = cloneDeep(this.fetchParams)
      // 重置分页
      this.listPage = {
        total: 0,
        page: 1,
        size: 10
      }
      // 清空表格数据
      this.tableList = []
      // 重置搜索表单
      this.searchForm = {}
    },

    confirm() {
      if (isFunction(this.normalizeOut)) {
        this.$emit('confirm', this.normalizeOut(this.selectionRow))
      } else {
        this.$emit('confirm', this.selectionRow)
      }

      this.closeDialog()
    },

    // 标签删除
    closeTag(index) {
      this.selectionRow.splice(index, 1)
    }
  }
}
</script>

<style lang="scss" scoped>
.invite-tags {
  ::v-deep .el-tag {
    margin-right: 10px;
    margin-bottom: 10px;
  }
}
</style>
